import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { HardDrive, Search, ShieldCheck, ShieldAlert, ShieldOff, AlertTriangle, Plus, Trash2, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || (window.location.origin + '/api');

const STATUS_CFG = {
  approved: { label: 'Aprobado',      color: 'var(--green)', bg: 'var(--green-dim)', icon: '✅' },
  graylist: { label: 'Lista gris',    color: 'var(--amber)', bg: 'var(--amber-dim)', icon: '⚠️' },
  blocked:  { label: 'Bloqueado',     color: 'var(--red)', bg: 'var(--red-dim)', icon: '🚫' },
  unknown:  { label: 'No registrado', color: 'var(--ink-2)', bg: 'var(--bg-subtle)', icon: '❓' },
};

const LIST_CFG = {
  whitelist: { label: 'Lista Blanca',  color: 'var(--green)', bg: 'var(--green-dim)', desc: 'Dispositivos corporativos aprobados' },
  graylist:  { label: 'Lista Gris',    color: 'var(--amber)', bg: 'var(--amber-dim)', desc: 'Conocidos pero no aprobados formalmente' },
  blacklist: { label: 'Lista Negra',   color: 'var(--red)', bg: 'var(--red-dim)', desc: 'Dispositivos bloqueados — alerta crítica' },
};

const MATCH_LABELS = { serial: 'Serial', vid_pid: 'VID:PID', device_id: 'Device ID' };

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function StatusBadge({ status }) {
  const cfg = STATUS_CFG[status] || STATUS_CFG.unknown;
  return (
    <span style={{ color: cfg.color, fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}>
      {cfg.icon} {cfg.label}
    </span>
  );
}

export default function UsbControl() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || (user?.extra_roles || []).includes('admin');
  const token   = localStorage.getItem('token');
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const [activeTab, setActiveTab] = useState('alerts');

  // ── Alertas ─────────────────────────────────────────────────────────────────
  const [alerts, setAlerts]       = useState([]);
  const [loadingAlerts, setLA]    = useState(true);

  const loadAlerts = useCallback(() => {
    setLA(true);
    fetch(`${API_URL}/usb/alerts`, { headers })
      .then(r => r.json()).then(d => setAlerts(Array.isArray(d) ? d : []))
      .finally(() => setLA(false));
  }, []); // eslint-disable-line

  // ── Listas ───────────────────────────────────────────────────────────────────
  const [lists, setLists]         = useState([]);
  const [loadingLists, setLL]     = useState(true);
  const [listTab, setListTab]     = useState('whitelist');
  const [showForm, setShowForm]   = useState(false);
  const [formData, setFormData]   = useState({ list_type: 'whitelist', match_type: 'serial', value: '', description: '' });
  const [saving, setSaving]       = useState(false);
  const [listError, setListError] = useState('');

  const loadLists = useCallback(() => {
    setLL(true);
    fetch(`${API_URL}/usb/lists`, { headers })
      .then(r => r.json()).then(d => setLists(Array.isArray(d) ? d : []))
      .finally(() => setLL(false));
  }, []); // eslint-disable-line

  // ── Búsqueda por serial ──────────────────────────────────────────────────────
  const [searchSerial, setSearchSerial] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching]         = useState(false);
  const [searched, setSearched]           = useState(false);

  const doSearch = async () => {
    if (!searchSerial.trim()) return;
    setSearching(true); setSearched(false);
    try {
      const r = await fetch(`${API_URL}/usb/search?serial=${encodeURIComponent(searchSerial.trim())}`, { headers });
      const d = await r.json();
      setSearchResults(Array.isArray(d) ? d : []);
    } catch { setSearchResults([]); }
    finally { setSearching(false); setSearched(true); }
  };

  // ── Stats ────────────────────────────────────────────────────────────────────
  const [stats, setStats] = useState(null);
  const loadStats = useCallback(() => {
    fetch(`${API_URL}/usb/stats`, { headers })
      .then(r => r.json()).then(d => setStats(d))
      .catch(() => {});
  }, []); // eslint-disable-line

  useEffect(() => { loadAlerts(); loadLists(); loadStats(); }, []); // eslint-disable-line

  // ── Guardar entrada en lista ──────────────────────────────────────────────────
  const handleAddToList = async (e) => {
    e.preventDefault();
    setSaving(true); setListError('');
    try {
      const r = await fetch(`${API_URL}/usb/lists`, {
        method: 'POST', headers, body: JSON.stringify({ ...formData, list_type: listTab }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error);
      setShowForm(false);
      setFormData({ list_type: 'whitelist', match_type: 'serial', value: '', description: '' });
      loadLists(); loadAlerts(); loadStats();
    } catch (err) { setListError(err.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta entrada?')) return;
    await fetch(`${API_URL}/usb/lists/${id}`, { method: 'DELETE', headers });
    loadLists(); loadAlerts(); loadStats();
  };

  const filteredLists = lists.filter(l => l.list_type === listTab);

  return (
    <div style={{ maxWidth: 1000 }}>
      {/* ── Header ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--gray-900)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <HardDrive size={22} style={{ color: 'var(--accent)' }} />
            Control de Dispositivos USB
          </div>
          <div style={{ fontSize: 13, color: 'var(--gray-500)' }}>
            Monitoreo de conexiones USB en equipos gestionados
          </div>
        </div>
        <button onClick={() => { loadAlerts(); loadLists(); loadStats(); }}
          style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 14px', fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, color: 'var(--ink-2)' }}>
          <RefreshCw size={14} /> Actualizar
        </button>
      </div>

      {/* ── Stats cards ── */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 24 }}>
          {[
            { label: 'Aprobados',      val: stats.approved, color: 'var(--green)', bg: 'var(--green-dim)', icon: <ShieldCheck size={18} /> },
            { label: 'Lista gris',     val: stats.graylist, color: 'var(--amber)', bg: 'var(--amber-dim)', icon: <ShieldAlert size={18} /> },
            { label: 'Bloqueados',     val: stats.blocked,  color: 'var(--red)', bg: 'var(--red-dim)', icon: <ShieldOff size={18} /> },
            { label: 'No registrados', val: stats.unknown,  color: 'var(--ink-2)', bg: 'var(--bg-subtle)', icon: <AlertTriangle size={18} /> },
          ].map(s => (
            <div key={s.label} className="card" style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: s.bg, color: s.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                {s.icon}
              </div>
              <div>
                <div style={{ fontSize: 22, fontWeight: 800, color: s.color }}>{s.val ?? '—'}</div>
                <div style={{ fontSize: 11, color: 'var(--ink-2)', fontWeight: 500 }}>{s.label}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Tabs ── */}
      <div style={{ display: 'flex', borderBottom: '2px solid var(--border)', marginBottom: 20, gap: 4 }}>
        {[
          { id: 'alerts', label: 'Alertas', badge: alerts.filter(a => a.status !== 'approved').length },
          { id: 'lists',  label: 'Listas de control' },
          { id: 'search', label: 'Buscar por serial' },
        ].map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            style={{ padding: '10px 18px', background: 'none', border: 'none', borderBottom: activeTab === t.id ? '2px solid #6366f1' : '2px solid transparent', color: activeTab === t.id ? 'var(--accent)' : 'var(--ink-2)', fontWeight: activeTab === t.id ? 700 : 400, fontSize: 13, cursor: 'pointer', marginBottom: -2, display: 'flex', alignItems: 'center', gap: 6 }}>
            {t.label}
            {t.badge > 0 && (
              <span style={{ color: 'var(--red)', fontSize: 11, fontWeight: 700 }}>{t.badge}</span>
            )}
          </button>
        ))}
      </div>

      {/* ── Tab: Alertas ── */}
      {activeTab === 'alerts' && (
        <div className="card">
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 16 }}>
            Dispositivos no aprobados detectados en la red
          </div>
          {loadingAlerts ? (
            <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
          ) : alerts.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 48, color: 'var(--ink-3)' }}>
              <ShieldCheck size={32} style={{ marginBottom: 8, color: 'var(--green)' }} />
              <div style={{ fontSize: 14, fontWeight: 600 }}>Sin alertas activas</div>
              <div style={{ fontSize: 12 }}>Todos los dispositivos USB están aprobados o aún no se detectaron</div>
            </div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Dispositivo</th>
                    <th>Serial</th>
                    <th style={{ width: 130 }}>Estado</th>
                    <th>Equipo</th>
                    <th style={{ width: 140 }}>Última vez</th>
                    <th style={{ width: 130 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {alerts.map(ev => (
                    <tr key={ev.id}>
                      <td>
                        <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink)' }}>
                          {ev.friendly_name || ev.device_id || '—'}
                        </div>
                        {ev.vendor_id && (
                          <div style={{ fontSize: 11, color: 'var(--ink-3)', fontFamily: 'monospace' }}>
                            VID:{ev.vendor_id} PID:{ev.product_id}
                          </div>
                        )}
                      </td>
                      <td><code style={{ fontSize: 11, background: 'var(--bg)', padding: '2px 6px', borderRadius: 4 }}>{ev.serial || '—'}</code></td>
                      <td><StatusBadge status={ev.status} /></td>
                      <td>
                        <div style={{ fontSize: 13, fontWeight: 500 }}>{ev.hostname}</div>
                        <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>{ev.user_name}</div>
                      </td>
                      <td style={{ fontSize: 12, color: 'var(--ink-2)' }}>{fmtDate(ev.last_seen || ev.reported_at)}</td>
                      <td>
                        {isAdmin && ev.serial && (
                          <button
                            onClick={() => {
                              setListTab('whitelist');
                              setFormData({ list_type: 'whitelist', match_type: 'serial', value: ev.serial, description: ev.friendly_name || '' });
                              setShowForm(true);
                              setActiveTab('lists');
                            }}
                            style={{ fontSize: 11, padding: '4px 10px', background: 'var(--green-dim)', color: 'var(--green)', border: '1px solid var(--green-dim)', borderRadius: 6, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                            + Aprobar
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Tab: Listas ── */}
      {activeTab === 'lists' && (
        <div>
          {/* Sub-tabs de lista */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', gap: 8 }}>
              {Object.entries(LIST_CFG).map(([key, cfg]) => (
                <button key={key} onClick={() => setListTab(key)}
                  style={{ padding: '7px 16px', borderRadius: 8, border: `1.5px solid ${listTab === key ? cfg.color : 'var(--border)'}`, background: listTab === key ? cfg.bg : '#fff', color: listTab === key ? cfg.color : 'var(--ink-2)', fontSize: 13, fontWeight: listTab === key ? 700 : 400, cursor: 'pointer' }}>
                  {cfg.label}
                  <span style={{ marginLeft: 6, color: listTab === key ? '#fff' : 'var(--ink-2)', fontSize: 11, fontWeight: 700 }}>
                    {lists.filter(l => l.list_type === key).length}
                  </span>
                </button>
              ))}
            </div>
            {isAdmin && (
              <button onClick={() => { setFormData({ match_type: 'serial', value: '', description: '' }); setShowForm(true); setListError(''); }}
                style={{ background: 'var(--ink)', color: 'var(--surface)', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                <Plus size={14} /> Agregar entrada
              </button>
            )}
          </div>

          {/* Descripción de la lista activa */}
          <div style={{ background: LIST_CFG[listTab].bg, border: `1px solid`, borderColor: LIST_CFG[listTab].color + '40', borderRadius: 10, padding: '10px 16px', marginBottom: 16, fontSize: 13, color: LIST_CFG[listTab].color }}>
            {LIST_CFG[listTab].desc}
          </div>

          {/* Formulario nueva entrada */}
          {showForm && isAdmin && (
            <div className="card" style={{ marginBottom: 16, padding: 20 }}>
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 14 }}>Nueva entrada — {LIST_CFG[listTab].label}</div>
              {listError && (
                <div style={{ background: 'var(--red-dim)', border: '1px solid var(--red-dim)', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: 'var(--red)', marginBottom: 12 }}>{listError}</div>
              )}
              <form onSubmit={handleAddToList}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 2fr', gap: 12, marginBottom: 12 }}>
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-2)', display: 'block', marginBottom: 4 }}>TIPO DE MATCH</label>
                    <select className="form-input" value={formData.match_type} onChange={e => setFormData(f => ({ ...f, match_type: e.target.value }))}>
                      <option value="serial">Serial</option>
                      <option value="vid_pid">VID:PID (ej: 0951:1666)</option>
                      <option value="device_id">Device ID (contiene)</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-2)', display: 'block', marginBottom: 4 }}>VALOR</label>
                    <input className="form-input" required value={formData.value} onChange={e => setFormData(f => ({ ...f, value: e.target.value }))}
                      placeholder={formData.match_type === 'serial' ? 'ej: 001CC0EC3462EB' : formData.match_type === 'vid_pid' ? 'ej: 0951:1666' : 'ej: Kingston'} />
                  </div>
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-2)', display: 'block', marginBottom: 4 }}>DESCRIPCIÓN</label>
                    <input className="form-input" value={formData.description} onChange={e => setFormData(f => ({ ...f, description: e.target.value }))}
                      placeholder="ej: USB corporativo Recursos Humanos" />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancelar</button>
                  <button type="submit" className="btn btn-primary" disabled={saving}
                    style={{ background: LIST_CFG[listTab].color }}>
                    {saving ? 'Guardando...' : `Agregar a ${LIST_CFG[listTab].label}`}
                  </button>
                </div>
              </form>
            </div>
          )}

          <div className="card">
            {loadingLists ? (
              <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
            ) : filteredLists.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: 'var(--ink-3)', fontSize: 13 }}>
                Sin entradas en {LIST_CFG[listTab].label.toLowerCase()}
              </div>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Tipo</th>
                      <th>Valor</th>
                      <th>Descripción</th>
                      <th style={{ width: 120 }}>Agregado por</th>
                      <th style={{ width: 130 }}>Fecha</th>
                      {isAdmin && <th style={{ width: 60 }}></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLists.map(entry => (
                      <tr key={entry.id}>
                        <td>
                          <span style={{ color: 'var(--ink-2)', fontSize: 11, fontWeight: 600 }}>
                            {MATCH_LABELS[entry.match_type]}
                          </span>
                        </td>
                        <td><code style={{ fontSize: 12, background: 'var(--bg)', padding: '2px 8px', borderRadius: 4 }}>{entry.value}</code></td>
                        <td style={{ fontSize: 12, color: 'var(--ink-2)' }}>{entry.description || '—'}</td>
                        <td style={{ fontSize: 12, color: 'var(--ink-2)' }}>{entry.created_by_name || '—'}</td>
                        <td style={{ fontSize: 12, color: 'var(--ink-2)' }}>{fmtDate(entry.created_at)}</td>
                        {isAdmin && (
                          <td>
                            <button onClick={() => handleDelete(entry.id)}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--red)', padding: 4 }}>
                              <Trash2 size={14} />
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Tab: Búsqueda por serial ── */}
      {activeTab === 'search' && (
        <div>
          <div className="card" style={{ padding: 20, marginBottom: 20 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 4 }}>Rastrear un dispositivo por número de serie</div>
            <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 14 }}>
              Útil en investigaciones forenses: ¿en qué equipos se conectó este pendrive?
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <input className="form-input" style={{ flex: 1 }}
                value={searchSerial} onChange={e => setSearchSerial(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && doSearch()}
                placeholder="Ej: 001CC0EC3462EB — parcial o completo" />
              <button onClick={doSearch} disabled={searching || !searchSerial.trim()}
                style={{ padding: '8px 20px', background: 'var(--ink)', color: 'var(--surface)', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, opacity: (!searchSerial.trim() || searching) ? 0.6 : 1 }}>
                <Search size={14} /> {searching ? 'Buscando...' : 'Buscar'}
              </button>
            </div>
          </div>

          {searched && searchResults !== null && (
            <div className="card">
              {searchResults.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 40, color: 'var(--ink-3)' }}>
                  <Search size={28} style={{ marginBottom: 8 }} />
                  <div style={{ fontSize: 14, fontWeight: 600 }}>Sin resultados</div>
                  <div style={{ fontSize: 12 }}>No se encontró este serial en ningún equipo gestionado</div>
                </div>
              ) : (
                <>
                  <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--bg)', fontSize: 13, color: 'var(--ink-2)', fontWeight: 600 }}>
                    {searchResults.length} equipo{searchResults.length !== 1 ? 's' : ''} conectaron este dispositivo
                  </div>
                  <div className="table-wrap">
                    <table>
                      <thead>
                        <tr>
                          <th>Equipo</th>
                          <th>Dispositivo</th>
                          <th style={{ width: 130 }}>Estado</th>
                          <th style={{ width: 130 }}>Primera vez</th>
                          <th style={{ width: 130 }}>Última vez</th>
                          <th style={{ width: 80, textAlign: 'center' }}>Conexiones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {searchResults.map(ev => (
                          <tr key={ev.id}>
                            <td>
                              <div style={{ fontWeight: 500, fontSize: 13 }}>{ev.hostname}</div>
                              <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>{ev.user_name}</div>
                            </td>
                            <td>
                              <div style={{ fontSize: 12 }}>{ev.friendly_name || ev.device_id || '—'}</div>
                              <code style={{ fontSize: 10, color: 'var(--ink-3)' }}>{ev.serial}</code>
                            </td>
                            <td><StatusBadge status={ev.status} /></td>
                            <td style={{ fontSize: 12, color: 'var(--ink-2)' }}>{fmtDate(ev.first_seen)}</td>
                            <td style={{ fontSize: 12, color: 'var(--ink-2)' }}>{fmtDate(ev.last_seen)}</td>
                            <td style={{ textAlign: 'center', fontWeight: 700, color: 'var(--ink-2)' }}>{ev.connection_count}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
